import { Component, inject, computed, OnDestroy, ViewChild, TemplateRef } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { CreditsService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { Subject, takeUntil } from "rxjs";
import { RedemptionConfirmationComponent } from "./components/redemption-confirmation/redemption-confirmation.component";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { AppleConfigService } from "@bitwarden/web-vault/app/services/api/apple-config.service";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { TransactionType } from "@bitwarden/web-vault/app/models/enum/credits.enum";
import { GLOSS_CREDITS_SYMBOL } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { RightPanelService } from "@bitwarden/web-vault/app/components/dialog/right-panel.service";
import { Capacitor } from "@capacitor/core";

interface RedemptionMilestone {
  label: string; // e.g. "$1", "$5"
  value: number; // in AUD or points – mock for now
  isCompleted?: boolean; // for styling the dots
}

interface RedemptionSection {
  id: "monthly" | "yearly";
  title: string;
  currentPoints: number;
  targetPoints: number;
  periodLabel?: string; // e.g. "1 mo. Free", "1 yr Free"
  availableMilestones: number;
  rewardSummary: string;
  milestones: RedemptionMilestone[];
}

@Component({
  selector: "app-points-redemption",
  templateUrl: "./points-redemption.component.html",
})
export class PointsRedemptionComponent implements OnDestroy {
  private readonly monthlyRedeemPoints = 1000;
  creditsService = inject(CreditsService);
  dialog: MatDialog = inject(MatDialog);
  private rightPanel = inject(RightPanelService);
  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private appleService: AppleConfigService = inject(AppleConfigService);
  private userStoreService: UserStoreService = inject(UserStoreService);
  protected dialogFactory: DialogFactory;
  private destroy$: Subject<any> = new Subject<any>();
  private currentDialogRef: MatDialogRef<DialogWrapperComponent> | null = null;
  private redeeming = false;

  @ViewChild("footerTemplate", { static: false }) footerTemplate!: TemplateRef<any>;
  private i18nService = inject(I18nService);

  protected saveButtonOptions = this.makeSaveButton(true);

  private makeSaveButton(isEnabled: boolean): ActionButton {
    return new ActionButton({
      text: this.i18nService.t("confirm"),
      isEnabled,
      type: "submit",
      onClick: this.redeem.bind(this),
      class:
        "tw-w-full tw-h-[60px] tw-rounded-full tw-bg-color-action-primary tw-shadow-button tw-font-medium tw-text-color-text-on-action tw-text-[20px]",
    });
  }

  constructor() {
    this.dialogFactory = new DialogFactory();
  }

  sections = computed<RedemptionSection[]>(() => {
    const currentPoints = this.creditsService.totalGoldCredits();

    return [
      {
        id: "monthly",
        title: "Monthly Discount",
        currentPoints: currentPoints,
        targetPoints: 1000,
        periodLabel: "1 mo. Free",
        availableMilestones: 1,
        rewardSummary: "Get one month free",
        // TODO: also have a value mapping in redemption-confirmation.component.ts and ensure they match?
        milestones: [
          // { label: "$1", value: 1000, isCompleted: currentPoints >= 1000 },
          // { label: "$6", value: 6000, isCompleted: currentPoints >= 6000 },
          { label: "Free", value: 1000, isCompleted: currentPoints >= 1000 },
        ],
      },
    ];
  });

  /**
   * Progress bar fill percentage
   * Uses targetPoints as the logical max
   */
  getSectionProgress(section: RedemptionSection): number {
    if (!section?.targetPoints) {
      return 0;
    }

    const ratio = section.currentPoints / section.targetPoints;
    const clamped = Math.max(0, Math.min(1, ratio));

    return clamped * 100;
  }

  rangeStart = computed(() => {
    const currentPoints = this.creditsService.totalGoldCredits();
    const start = Math.floor(currentPoints / this.monthlyRedeemPoints) * this.monthlyRedeemPoints;
    return start;
  });

  rangeEnd = computed(() => {
    const end = this.rangeStart() + this.monthlyRedeemPoints;
    return end;
  });

  getPointsPosition(currentPoints: number): number {
    const rangeStart = this.rangeStart();
    const rangeEnd = this.rangeEnd();
    const rangeSize = rangeEnd - rangeStart;

    if (rangeSize === 0) {
      return 0;
    }

    const offset = currentPoints - rangeStart;
    const percentage = (offset / rangeSize) * 100;

    return Math.max(0, Math.min(100, percentage));
  }

  /**
   * Core numeric position computation (0–100),
   * proportional to milestone.value over [minValue, maxValue].
   */
  private computeMilestonePosition(section: RedemptionSection, index: number): number {
    const milestones = section.milestones;
    if (!milestones || milestones.length === 0) {
      return 0;
    }

    if (milestones.length === 1) {
      return 96;
    }

    const values = milestones.map((m) => m.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    if (minValue === maxValue) {
      return 50;
    } // all equal → center

    const value = milestones[index].value;

    const t = (value - minValue) / (maxValue - minValue); // 0–1

    const padding = 4; // % padding on each side
    const usable = 100 - padding * 2;

    const raw = padding + t * usable;
    const clamped = Math.max(0, Math.min(raw, 100));

    return clamped;
  }

  /**
   * For CSS left: "<percent>%"
   */
  getMilestonePosition(section: RedemptionSection, index: number): string {
    return `${this.computeMilestonePosition(section, index)}%`;
  }

  /**
   * Whether to show the label under this milestone.
   * - Always show first and last
   * - Hide “middle” labels that are too close to the last visible one
   */
  shouldShowMilestoneLabel(section: RedemptionSection, index: number): boolean {
    const milestones = section.milestones;
    if (!milestones || milestones.length === 0) {
      return false;
    }

    const count = milestones.length;
    if (count === 1) {
      return true;
    }

    const threshold = 6; // % difference needed to show label

    // Always show first and last labels
    if (index === 0 || index === count - 1) {
      return true;
    }

    // Scan from start to this index, simulating "show if far enough"
    let lastVisiblePos = this.computeMilestonePosition(section, 0);

    for (let i = 1; i <= index; i++) {
      const pos = this.computeMilestonePosition(section, i);
      const isLast = i === count - 1;
      const farEnough = pos - lastVisiblePos >= threshold;

      if (farEnough || isLast) {
        if (i === index) {
          return true;
        }
        lastVisiblePos = pos;
      } else {
        if (i === index) {
          return false;
        }
      }
    }

    return true;
  }

  isMonthAffordable = computed(() => {
    return this.creditsService.totalGoldCredits() >= 1000;
  });

  confirmRedemption(): void {
    const canAffordOneMonth = this.isMonthAffordable();
    if (!canAffordOneMonth) {
      this.notifyService.notify(NotificationEnum.error, "failed", "notAvailableCredits");
      return;
    }

    // Fresh enabled Confirm every time the dialog opens, so the success path in redeem() can
    // leave the button disabled through the exit animation without stranding it on reopen.
    this.saveButtonOptions = this.makeSaveButton(true);

    // we need to open up a dialog like when we create a category or account but it will contain redemption info
    // Click-away and Escape close it (RightPanelService), except while a redemption request is in
    // flight.
    const ref = this.rightPanel.open(
      DialogWrapperComponent,
      {
        data: {
          title: "confirmRedemption",
          showBackButton: false,
          component: RedemptionConfirmationComponent,
          fullyCustomised: true,
          showHeaderCloseButton: true,
          footerTemplate: this.footerTemplate,
        },
      },
      { isBusy: () => this.redeeming },
    );

    this.currentDialogRef = ref;
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // A dialog reopened during this one's exit animation already owns currentDialogRef.
        if (this.currentDialogRef === ref) {
          this.currentDialogRef = null;
        }
      });
  }

  async redeem(): Promise<void> {
    // A reopened dialog gets a fresh enabled Confirm even mid-request; block a second WITHDRAWAL.
    if (this.redeeming) {
      return;
    }
    this.redeeming = true;
    let succeeded = false;
    try {
      const isWeb = Capacitor.getPlatform() === "web";
      // Read live: null until the subscription has loaded.
      // Stop rather than fall through to auto redemption, which the server rejects for Apple.
      const sub = this.userStoreService.subscription.activeSubscription();
      if (!sub) {
        this.notifyService.notify(NotificationEnum.error, "errorOccurred", "failedToRedeem");
        return;
      }
      const isIOSSubscription = sub.paymentProvider === "apple";

      if (isWeb && isIOSSubscription) {
        this.notifyService.notify(NotificationEnum.error, "failed", "iosRedeemOnPhone");
        return;
      }

      const accountt = this.creditsService.accounts()[0];
      const isCapableIosCreditRedemption =
        this.appleService.iOSIAPCapability && (sub.tier === "free" || isIOSSubscription);
      const amount = this.creditsService.creditAmountToRedeem();
      if (amount <= 0 || amount > this.creditsService.totalGoldCredits()) {
        this.notifyService.notify(NotificationEnum.error, "failed", "notAvailableCredits");
        return;
      }

      // Only open the iOS redemption flow on iOS platforms. The code is claimed first, so there's
      // one to give before any points are taken: the server answers an empty pool (or its own
      // failure) with an empty code, not an error (#6044).
      let latestOfferCode: string | undefined;
      if (isCapableIosCreditRedemption) {
        latestOfferCode = await this.appleService.getOfferCode();
        if (typeof latestOfferCode !== "string" || !latestOfferCode.trim()) {
          throw new Error("no offer code available");
        }
      }

      await this.creditsService.createTransaction(
        accountt._id,
        TransactionType.WITHDRAWAL,
        GLOSS_CREDITS_SYMBOL,
        amount,
        !isCapableIosCreditRedemption,
      );
      // The WITHDRAWAL is committed here, whatever happens after.
      succeeded = true;

      // Shown only once the points are taken: a failed WITHDRAWAL leaves no usable code on screen
      // (the claimed code goes unused) (#6044).
      if (latestOfferCode) {
        try {
          this.dialogFactory.openIosRedemptionFlow(latestOfferCode);
        } catch {
          // The points are taken: still a success, not "failedToRedeem" and a retry.
        }
      }

      // Show success notification
      this.notifyService.notify(
        NotificationEnum.success,
        "succeeded",
        "bundleSubscriptionRedeemed",
      );

      // Close the dialog
      if (this.currentDialogRef) {
        this.currentDialogRef.close();
      }
    } catch (e) {
      this.notifyService.notify(NotificationEnum.error, "errorOccurred", "failedToRedeem");
    } finally {
      this.redeeming = false;
      // A new instance (not enableButton(true) in place) so the OnPush GlossButtonComponent
      // actually re-renders — mutating the existing object leaves the button stuck disabled.
      // Stays disabled after a success: the footer remains mounted and clickable for the
      // dialog's 200ms exit animation, and a second click there would issue a second
      // WITHDRAWAL. confirmRedemption() re-enables it on the next open.
      this.saveButtonOptions = this.makeSaveButton(!succeeded);
    }
  }

  cancel(): void {
    // Close the dialog without any action
    if (this.currentDialogRef) {
      this.currentDialogRef.close();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
